import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Building2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ClientForm } from "@/components/forms/ClientUserForms";
import { DeleteClientButton } from "@/components/DeleteClientButton";
import { TableSkeleton } from "@/components/Skeleton";
import { api } from "@/lib/api";

export default function ClientsListPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients/with-stats");
      setClients(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Organizations</h2>
          <p className="text-muted-foreground">Companies storing products in the Dolphin warehouse</p>
        </div>
        <ClientForm onSaved={load} />
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? <TableSkeleton rows={5} cols={7} /> :
            clients.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50/50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3 font-medium">Organization</th>
                      <th className="px-4 py-3 font-medium">Contact</th>
                      <th className="px-4 py-3 font-medium">In Warehouse</th>
                      <th className="px-4 py-3 font-medium">Total Products</th>
                      <th className="px-4 py-3 font-medium">Active Shipments</th>
                      <th className="px-4 py-3 font-medium">Crates</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => {
                      const st = c.stats || {};
                      return (
                        <tr key={c.id} className="border-b last:border-0 transition-colors hover:bg-slate-50">
                          <td className="px-4 py-3"><Link to={`/clients/${c.id}`} className="font-medium text-sky-700 hover:text-sky-900 hover:underline">{c.name}</Link></td>
                          <td className="px-4 py-3">{c.contact_name || "—"}{c.contact_email && (<span className="block text-xs text-muted-foreground">{c.contact_email}</span>)}</td>
                          <td className="px-4 py-3">{st.in_warehouse || 0}</td>
                          <td className="px-4 py-3">{st.total_products || 0}</td>
                          <td className="px-4 py-3">{st.active_shipments || 0}</td>
                          <td className="px-4 py-3">{st.crates || 0}</td>
                          <td className="px-4 py-3">{c.is_archived ? <Badge variant="secondary">Archived</Badge> : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200" variant="outline">Active</Badge>}</td>
                          <td className="px-4 py-3 text-right">
                            <DeleteClientButton client={c} onDeleted={load} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
                <Building2 className="h-5 w-5" />
                <p>No organizations yet. Add your first organization to get started.</p>
              </div>
            )
          }
        </CardContent>
      </Card>
    </div>
  );
}
